<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
    <!-- Título de página -->
    <div class="mb-4">
      <h2 class="mt-lg-4 mb-1 pb-1" style="font-family: OpenSans-bold">
        <strong>Carga masiva de faciales</strong>
      </h2>
    </div>

    <!-- Pantalla informativa para fracs sin acceso -->
    <FeatureUnavailable v-if="!hasFacialRecognition" />

    <!-- Contenido principal - Solo visible si tiene reconocimiento facial -->
    <template v-else>
      <!-- Stepper mejorado -->
    <nav class="relative mb-6 sm:mb-8" aria-label="Progreso">
      <div class="flex items-center justify-between">
        <!-- Paso 1 -->
        <div class="flex flex-col items-center flex-1">
          <div :class="bulletClass(1)"
            class="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium mb-2 transition-all">
            <svg v-if="currentStep > 1" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span v-else>1</span>
          </div>
          <span :class="labelClass(1)" class="font-medium text-xs sm:text-sm text-center">Subir ZIP</span>
        </div>

        <!-- Línea conectora 1-2 -->
        <div class="flex-1 h-1 mx-2 sm:mx-4 rounded" :class="currentStep > 1 ? 'bg-green-500' : 'bg-gray-200'"></div>

        <!-- Paso 2 -->
        <div class="flex flex-col items-center flex-1">
          <div :class="bulletClass(2)"
            class="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium mb-2 transition-all">
            <svg v-if="currentStep > 2" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span v-else>2</span>
          </div>
          <span :class="labelClass(2)" class="font-medium text-xs sm:text-sm text-center leading-tight">Revisar<br
              class="hidden sm:inline" /><span class="sm:hidden"> y </span>validar</span>
        </div>

        <!-- Línea conectora 2-3 -->
        <div class="flex-1 h-1 mx-2 sm:mx-4 rounded" :class="currentStep > 2 ? 'bg-green-500' : 'bg-gray-200'"></div>

        <!-- Paso 3 -->
        <div class="flex flex-col items-center flex-1">
          <div :class="bulletClass(3)"
            class="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium mb-2 transition-all">
            <svg v-if="currentStep > 3" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span v-else>3</span>
          </div>
          <span :class="labelClass(3)" class="font-medium text-xs sm:text-sm text-center">Completado</span>
        </div>
      </div>
    </nav>

    <!-- Panel de ayuda contextual -->
    <div v-if="helpText" class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div class="flex gap-3">
        <svg class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div class="text-sm">
          <p class="font-medium text-blue-900 mb-1">{{ helpText.title }}</p>
          <!-- <p class="text-blue-700">{{ helpText.description }}</p> -->
          <p class="text-blue-700" v-html="helpText.description"></p>
        </div>
      </div>
    </div>

    <!-- Body -->
    <section class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 min-h-[400px]">
      <IngestZipForm v-if="currentStep === 1" :token="token" :base-url="baseUrl" :max-size-mb="500"
        :identification-type.sync="identificationType" @uploaded="onZipUploaded" @error="onError" />

      <IngestProgress v-if="currentStep === 2 && showProgress" :token="token" :base-url="baseUrl" :job-id="jobId"
        @done="onStreamDone" @failed="onStreamFailed" @error="onError" />

      <IngestResultBoard ref="resultBoard" v-if="currentStep === 2 && !showProgress" :token="token" :base-url="baseUrl"
        :job-id="jobId" @stats-updated="onStatsUpdated" @ingest-completed="onIngestCompleted" @error="onError" />

      <div v-if="currentStep === 3" class="text-center py-12">
        <svg class="w-16 h-16 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">¡Importación completada!</h2>
        <p class="text-gray-600 mb-6">
          {{ confirmationResult.activated || stats.approved }} fotos han sido activadas exitosamente
        </p>
        <button @click="resetWizard" class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
          Nueva importación
        </button>
      </div>
    </section>

    <!-- Navegación inferior -->
    <div v-if="currentStep < 3" class="flex flex-col sm:flex-row justify-between gap-3">
      <button v-if="currentStep === 2 && !showProgress" @click="cancelAndReset"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium flex items-center justify-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Cancelar
      </button>
      <div v-else></div>

      <div class="text-sm text-gray-500 text-center sm:text-right" v-if="currentStep === 1">
        Formato: ZIP | Máx: 500MB
      </div>

      <!-- Botón Cargar Faciales -->
      <button v-if="currentStep === 2 && !showProgress" @click="confirmIngest"
        class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        Cargar Faciales
      </button>
    </div>

    <!-- Botones de acción flotantes (solo en paso 2, hidden en móvil si hay bulk actions bar) -->
    <!-- <div v-if="currentStep === 2 && !showProgress"
      class="fixed bottom-6 right-6 flex flex-col gap-3 z-30 hidden lg:flex">
      <button v-if="stats.ready > 0 && stats.pending > 0" @click="approveAllReady"
        class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 font-medium">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        Marcar listos ({{ stats.ready }})
      </button>

      <button v-if="stats.approved > 0" @click="confirmIngest"
        class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 font-medium">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Activar fotos ({{ stats.approved }})
      </button>
    </div> -->
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import Swal from 'sweetalert2'

import IngestZipForm from './IngestZipForm.vue'
import IngestProgress from './IngestProgress.vue'
import IngestResultBoard from './IngestResultBoard.vue'
import FeatureUnavailable from '@/core/FeatureUnavailable.vue'

export default {
  name: 'FacialIngest',
  components: { IngestZipForm, IngestProgress, IngestResultBoard, FeatureUnavailable },
  props: {
    token: { type: String, required: true },
    baseUrl: { type: String, default: '/api/residents/facial-recognition' }
  },
  setup() {
    const instance = getCurrentInstance()
    const store = instance.proxy.$store

    // Verificar capacidades del fraccionamiento
    const hasFacialRecognition = computed(() => store.getters.hasFacialRecognition)

    onMounted(async () => {
      await store.dispatch('fetchFracCapabilities')
    })
    const currentStep = ref(1)
    const identificationType = ref('nombre')
    const jobId = ref(null)
    const showProgress = ref(true)
    const confirmationResult = ref({})
    const resultBoard = ref(null)

    const stats = ref({
      total: 0,
      failures: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      ready: 0,
      unmapped: 0
    })

    // Texto de ayuda contextual
    const helpText = computed(() => {
      const texts = {
        1: {
          title: 'Paso 1: Selecciona tu archivo ZIP',
          description: 'Sube un archivo ZIP que contenga las imágenes faciales. El nombre de cada imagen debe coincidir con el nombre del residente.'
        },
        2: showProgress.value ? {
          title: 'Procesando imágenes...',
          description: 'Estamos analizando y asignando las imágenes a los residentes correspondientes. Este proceso puede tomar unos minutos dependiendo del tamaño del archivo.'
        } : {
          title: 'Validar y Activar Imágenes Faciales',
          description: `Verifica la asignación de cada imagen. <br> Una vez confirmada, confirma la imagen. <br> Finalmente, haz clic en Guardar Cambios para aplicar las modificaciones.`
        },
        3: null
      }
      return texts[currentStep.value]
    })

    // Estilos stepper
    const bulletClass = (step) => {
      if (currentStep.value > step) return 'bg-green-500 ring-4 ring-green-100'
      if (currentStep.value === step) return 'bg-blue-600 ring-4 ring-blue-100'
      return 'bg-gray-300'
    }

    const labelClass = (step) => {
      return currentStep.value >= step ? 'text-gray-900' : 'text-gray-500'
    }

    // Handlers
    const onZipUploaded = ({ jobId: id }) => {
      jobId.value = id
      showProgress.value = true
      currentStep.value = 2
    }

    const onStreamDone = () => {
      showProgress.value = false
      Swal.fire({
        icon: 'success',
        title: 'Procesamiento completado',
        text: 'Ahora puedes revisar y validar las imágenes.',
        timer: 2000,
        showConfirmButton: false
      })
    }

    const onStreamFailed = ({ error }) => {
      Swal.fire({
        icon: 'error',
        title: 'Error en el procesamiento',
        text: error || 'No se pudo procesar el ZIP.',
      })
      currentStep.value = 1
      jobId.value = null
    }

    const onStatsUpdated = (next) => {
      stats.value = { ...stats.value, ...next }
    }

    const onIngestCompleted = (result) => {
      confirmationResult.value = result
      currentStep.value = 3
    }

    const onError = (msg) => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: msg || 'Ocurrió un error inesperado.',
      })
    }

    const resetWizard = () => {
      currentStep.value = 1
      jobId.value = null
      showProgress.value = true
      stats.value = {
        total: 0,
        failures: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        ready: 0,
        unmapped: 0
      }
      confirmationResult.value = {}
    }

    // Llamar métodos del IngestResultBoard
    const approveAllReady = () => {
      if (resultBoard.value) {
        resultBoard.value.handleApproveAllReady()
      }
    }

    const confirmIngest = () => {
      if (resultBoard.value) {
        resultBoard.value.handleConfirmIngest()
      }
    }

    const cancelAndReset = async () => {
      try {
        const result = await Swal.fire({
          title: '¿Cancelar proceso?',
          text: 'Se perderán todos los cambios no guardados.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Sí, cancelar',
          cancelButtonText: 'No'
        })

        if (result.value) {
          resetWizard()
        }
      } catch (error) {
        console.error('Error in cancelAndReset:', error)
      }
    }

    return {
      hasFacialRecognition,
      currentStep,
      identificationType,
      jobId,
      showProgress,
      stats,
      confirmationResult,
      resultBoard,
      helpText,
      bulletClass,
      labelClass,
      onZipUploaded,
      onStreamDone,
      onStreamFailed,
      onStatsUpdated,
      onIngestCompleted,
      onError,
      resetWizard,
      approveAllReady,
      confirmIngest,
      cancelAndReset
    }
  }
}
</script>

<style scoped>
/* Scrollbar suave */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>